const trustedTypesPolicy = window.trustedTypes?.createPolicy?.("session-script-src", {
    createScriptURL: value => value,
});
const asyncScript = (src, {onStart, onSuccess} = {}) => {
    if (!src) {
        return;
    }

    const nonce = document.currentScript?.nonce || document.querySelector("script[nonce]")?.nonce;
    const script = document.createElement("script");
    script.async = true;

    if (nonce) {
        script.nonce = nonce;
    }

    if (onStart) {
        onStart(script);
    }

    script.src = trustedTypesPolicy?.createScriptURL(src) ?? src;

    if (onSuccess) {
        const onLoad = () => {
            if (onSuccess) {
                onSuccess(script);
                onSuccess = undefined;
            }
        };
        const onReadyStateChange = event =>
            [
                "complete",
                "loaded",
            ].includes(event.readyState) && onLoad();

        script.addEventListener("load", onLoad);
        script.addEventListener("readystatechange", onReadyStateChange);
    }

    requestAnimationFrame(() => (document.head || document.body).append(script));
};

let sessionStarted = false;
let onlineHandler;

const startSession = () => {
    if (sessionStarted || !navigator.onLine || navigator.globalPrivacyControl) {
        return;
    }
    if (onlineHandler) {
        window.removeEventListener("online", onlineHandler);
        onlineHandler = undefined;
    }

    sessionStarted = true;

    // Sentry https://browser.sentry-cdn.com/.../bundle.min.js
    asyncScript("/js/sentry/bundle.min.js", {
        onSuccess() {
            window.Sentry?.init({
                dsn: "https://7cbefafebda548608cd083c93f0ae3ec@o72582.ingest.us.sentry.io/1465689",
                tunnel: "/api/error",
                tracesSampleRate: 0.05,
                dataCollection: {
                    userInfo: false,
                    httpHeaders: false,
                    urlQueryParams: false,
                },
                beforeSend(event) {
                    const urlAtTime = window.FS?.getCurrentSessionURL?.(true);

                    if (urlAtTime) {
                        event.contexts = {
                            ...(event.contexts ?? {}),
                            fullstory: {
                                urlAtTime,
                            },
                        };
                    }
                    if (event.user?.ip_address) {
                        delete event.user.ip_address;
                    }
                    return event;
                },
            });
        },
    });

    const zarazData = {
        t: document.title,
        x: Math.random(),
        w: screen.width,
        h: screen.height,
        j: innerHeight,
        e: innerWidth,
        l: location.href,
        r: document.referrer,
        k: screen.colorDepth,
        n: document.characterSet,
        o: new Date().getTimezoneOffset(),
    };
    fetch("/api/pulse", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        keepalive: true,
        body: JSON.stringify({
            name: "pageview",
            data: zarazData,
            zarazData,
        }),
    }).catch(() => {
        // Ignore network errors - this is a non-critical analytics call
    });

    asyncScript("https://edge.fullstory.com/s/fs.js", {
        onStart() {
            window._fs_host = "fullstory.com";
            window._fs_script = "edge.fullstory.com/s/fs.js";
            window._fs_org = "18Z2HP";
            window._fs_namespace = "FS";
            const g = (window[window._fs_namespace] = function (b, d, j, r, i, c) {
                if (g._api) {
                    return g._api(b, d, j, r, i, c);
                }
                if (g.q) {
                    g.q.push([
                        b,
                        d,
                        j,
                        r,
                        i,
                        c,
                    ]);
                }
                return null;
            });
            g.q = [];
            g.identify = function (i, v, s) {
                g(
                    "setProperties",
                    {
                        type: "user",
                        properties: {
                            uid: i,
                        },
                    },
                    s,
                );
                if (v) {
                    g(
                        "setProperties",
                        {
                            type: "user",
                            properties: v,
                        },
                        s,
                    );
                }
            };
            g.setUserVars = function (v, s) {
                g(
                    "setProperties",
                    {
                        type: "user",
                        properties: v,
                    },
                    s,
                );
            };
            g.event = function (i, v, s) {
                g(
                    "trackEvent",
                    {
                        name: i,
                        properties: v,
                    },
                    s,
                );
            };
            g.anonymize = function () {
                g.identify(false);
            };
            g.shutdown = function () {
                g("shutdown");
            };
            g.restart = function () {
                g("restart");
            };
            g.log = function (a, b) {
                g("log", {
                    level: a,
                    msg: b,
                });
            };
            g.consent = function (a) {
                g("setIdentity", {
                    consent: !arguments.length || a,
                });
            };
            g.identifyAccount = function (i, v) {
                v = v || {};
                v.acctId = i;
                g("setProperties", {
                    type: "account",
                    properties: v,
                });
            };
            g.clearUserCookie = function () {};
            g.setVars = function (n, p) {
                g("setProperties", {
                    type: n,
                    properties: p,
                });
            };
            g._w = {};
            g._w.XMLHttpRequest = window.XMLHttpRequest;
            g._w.fetch = window.fetch;
            if (window.fetch) {
                window.fetch = function () {
                    return g._w.fetch.apply(this, arguments);
                };
            }
            g._v = "2.0.0";
        },
    });
};
const session = () => {
    if (sessionStarted || navigator.globalPrivacyControl) {
        return;
    }
    if (navigator.onLine) {
        startSession();
        return;
    }
    if (!onlineHandler) {
        onlineHandler = () => startSession();
        window.addEventListener("online", onlineHandler, {
            once: true,
        });
    }
};

export {session};
